import GavelIcon from '@mui/icons-material/Gavel';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCountdown } from '../hooks/useCountdown';
import { formatCountdown, formatTokens } from '../lib/format';
import { KIND_LABELS, type Market } from '../schemas/market.schema';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';
import { PoolBar } from './PoolBar';

interface MarketCardProps {
    market: Market;
}

export function MarketCard({ market }: MarketCardProps) {
    const remaining = useCountdown(market.status === 'open' ? market.closesAt : null);
    const session = useAuthStore((s) => s.session);

    // The person who decides this one, while it still needs deciding.
    const mineToSettle =
        session?.id === market.resolverId &&
        market.status !== 'settled' &&
        market.status !== 'void';

    return (
        <Paper sx={{ p: 1.75 }}>
            <Box
                component={RouterLink}
                to={`/markets/${market.id}`}
                sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
            >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} mb={1}>
                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: market.status === 'open' ? colors.safety : colors.muted,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {market.status === 'open'
                        ? `Closes ${formatCountdown(remaining)}`
                        : STATUS_LABELS[market.status]}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.63rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: colors.muted,
                    }}
                >
                    {KIND_LABELS[market.kind]}
                </Typography>
            </Stack>

                <Typography
                    sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3, mb: 0.5 }}
                >
                    {market.question}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.66rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: colors.muted,
                        mb: 1.2,
                    }}
                >
                    {market.creatorHandle} put this up
                    {market.resolverHandle !== market.creatorHandle &&
                        ` · ${market.resolverHandle} decides`}
                </Typography>

                {market.kind === 'closest' ? (
                    <Typography
                        sx={{ fontFamily: mono, fontSize: '0.8rem', color: colors.inkSoft }}
                    >
                        {market.bettors} {market.bettors === 1 ? 'guess' : 'guesses'} so far
                    </Typography>
                ) : (
                    <PoolBar market={market} />
                )}

            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                    borderTop: `1px solid ${colors.line}`,
                    mt: 1.2,
                    pt: 0.9,
                    fontFamily: mono,
                    fontSize: '0.66rem',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: colors.muted,
                }}
            >
                <span>
                    {market.bettors} in
                    {market.myStake > 0 && ` · you ${formatTokens(market.myStake)}`}
                </span>
                <span>
                    {market.kind === 'free_for_all' ? 'Pot ' : 'Pot '}
                    <Typography component="span" sx={{ color: colors.safety, fontWeight: 600, fontFamily: mono, fontSize: 'inherit' }}>
                        {formatTokens(market.pot)}
                    </Typography>
                </span>
            </Stack>

                {market.status === 'void' && (
                    <Chip
                        label="Called off — tokens returned"
                        size="small"
                        sx={{ mt: 1, bgcolor: colors.raised, color: colors.muted }}
                    />
                )}
            </Box>

            {mineToSettle && (
                <Button
                    component={RouterLink}
                    to={`/markets/${market.id}/settle`}
                    variant="contained"
                    fullWidth
                    startIcon={<GavelIcon />}
                    sx={{ mt: 1.5 }}
                >
                    {market.status === 'open' ? 'Settle early' : 'Say what happened'}
                </Button>
            )}
        </Paper>
    );
}

const STATUS_LABELS: Record<Market['status'], string> = {
    open: 'Open',
    locked: 'Betting shut — waiting on the answer',
    resolving: 'Working it out',
    settled: 'Done',
    void: 'Called off',
};
